/**
 * RYTE Goal types — derived from the RYARYTEEngine Smithy model.
 *
 * A Goal declares operational intent for a trailer at a yard.
 * Goals are the pills/bars on the Gantt timeline.
 *
 * @see Smithy model: com.amazon.ryaryteengine
 */

// ─── Enums ───────────────────────────────────────────────────────────────────

export enum Intent {
  LOAD = "LOAD",
  UNLOAD = "UNLOAD",
  REMOVE = "REMOVE",
  ASSIGN_TRAILER = "ASSIGN_TRAILER",
  UNASSIGN_TRAILER = "UNASSIGN_TRAILER",
}

export enum GoalStatus {
  CREATED = "CREATED",
  HELD = "HELD",
  ACTIVATING = "ACTIVATING",
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELED = "CANCELED",
}

export enum StateUpdateRequestType {
  CANCEL = "CANCEL",
  HOLD = "HOLD",
  RELEASE = "RELEASE",
}

export enum LocationType {
  LOCATION = "LOCATION",
  LOCATION_TYPE = "LOCATION_TYPE",
  ZONE = "ZONE",
}

/**
 * Per-goal failure policy. Sent on eligible `createGoal` requests so the
 * engine knows what to do when a goal blows past its `executionWindow.delayedAfter`.
 *
 * - `FAIL_IF_DELAYED` — engine transitions the goal to `FAILED` (with
 *   `statusReason: FAILURE_MODE_DELAYED`) once the deadline passes, and
 *   cascade-fails any goals that reference it via `dependentGoalIds`.
 *
 * Absence is equivalent to the engine's default `NOTHING` mode — the goal
 * flips `isDelayed: true` past the deadline but stays alive. Only the one
 * active literal is modeled; the default is expressed by omitting the field.
 */
export enum FailureMode {
  FAIL_IF_DELAYED = "FAIL_IF_DELAYED",
}

export enum NotificationEventType {
  CREATED = "CREATED",
  UPDATED = "UPDATED",
  ACTIVATED = "ACTIVATED",
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELED = "CANCELED",
  HELD = "HELD",
  RELEASED = "RELEASED",
  DELAYED = "DELAYED",
}

// ─── Data Structures ─────────────────────────────────────────────────────────

export interface YardId {
  yardId: string;
}

export interface BuildingCode {
  buildingCode: string;
}

/** Callers may provide either a yardId or a buildingCode. */
export type RequestYardId = { yardId: YardId } | { buildingCode: BuildingCode };

export interface GoalId {
  id: string;
}

/**
 * Demand identifier for a goal.
 *
 * Operationally, a goal can carry up to two layers of identity:
 *   - A *primary* demand — VRID (haul/route), ISA (inbound shipment
 *     appointment), or locationPlanId. Describes WHAT work to do.
 *   - An optional *visit* — describes WHICH physical trailer to execute
 *     against. REMOVE may carry it beside the primary. LOAD/UNLOAD use it
 *     only when no primary demand is available.
 *
 * Wire shape: a record where each key is optional and at least one
 * MUST be present. The backend echoes back a "flat with nulls" shape
 * (every key present, inactive ones `null`) — `resolveDemandId` and
 * its sibling helpers handle both shapes uniformly.
 *
 * The union accepts both shapes because REMOVE and backend reads remain
 * two-layer. Intent-specific write normalization lives at the API boundary.
 */
type PrimaryDemand =
  | { VRID: { id: string }; ISA?: never; locationPlanId?: never }
  | { ISA: { id: string }; VRID?: never; locationPlanId?: never }
  | { locationPlanId: { id: string }; VRID?: never; ISA?: never };

export type DemandId =
  | (PrimaryDemand & { visitId?: { id: string } })
  | { visitId: { id: string }; VRID?: never; ISA?: never; locationPlanId?: never };

export interface Location {
  id: string;
  type: LocationType;
}

export interface ExecutionWindow {
  /** Epoch ms. Omitted when the goal has no scheduled start time (open-ended). */
  start?: number;
  /** Time after which the goal is considered delayed (epoch millis). */
  delayedAfter?: number;
}

export interface GoalParameters {
  executionWindow?: ExecutionWindow;
  locations?: Location[];
  dependentGoalIds?: string[];
  /**
   * Failure policy for the goal. Plain creates omit it for every intent. A
   * caller that intentionally owns the policy uses the
   * `failureModePolicy: "caller-specified"` wrapper to preserve an exact value.
   * Ordinary updates that replace parameters pass through the stored value.
   */
  failureMode?: FailureMode;
}

/** Complete goal record from the API. */
export interface Goal {
  goalId: GoalId;
  yardId: YardId;
  intent: Intent;
  demandId: DemandId;
  parameters?: GoalParameters;
  status: GoalStatus;
  statusReason?: string;
  associatedJobIds?: string[];
  /** Resolved in-yard Visit associated by the activation engine. */
  visitId?: string | null;
  version: number;
  requester: string;
  lastUpdatedBy?: string;
  isDelayed: boolean;
  stateUpdateRequest?: StateUpdateRequestType;
  /** DynamoDB marker: 1 when goal is non-terminal; absent for terminal goals. */
  activeGoalMarker?: number;
  createdAt: number; // epoch millis
  updatedAt: number; // epoch millis
  /** TTL epoch seconds for DynamoDB auto-deletion. */
  ttl?: number;
  /** Caller system that created the goal (IAM-role-derived). Write-once. */
  createdBySystem?: string;
  /** Caller system of the most recent mutation. Rewritten on every update. */
  lastUpdatedBySystem?: string;
  /** Operator-supplied justification when a goal state was overridden. */
  overrideReason?: string;
}

/**
 * SNS notification wire format for goal lifecycle events.
 * Message processor consumption is deferred to a separate spec.
 */
export interface GoalNotificationMessage {
  yardId: YardId;
  eventType: NotificationEventType;
  goalId: GoalId;
  timestamp: number;
  eventId: string;
  goalBeforeUpdate?: Goal;
  goalAfterUpdate?: Goal;
}

// ─── API Request/Response Types ──────────────────────────────────────────────

export interface CreateGoalRequest {
  requestYardId: RequestYardId;
  intent: Intent;
  demandId: DemandId;
  parameters?: GoalParameters;
  requester: string;
  /** When true, goal is created in HELD status instead of CREATED. */
  createAsHeld?: boolean;
}

/**
 * Lambda envelope wrapping a Goal record.
 * Used by createYardGoal, updateYardGoal, and getYardGoals responses.
 *
 * Real API shape: { data: { goal: Goal }, goalId: string, yardId: string }
 * The Goal is double-nested under data.goal.
 */
export interface GoalEnvelope {
  data: { goal: Goal };
  goalId: string;
  yardId: string;
}

/**
 * Lambda response wraps the Goal in an envelope.
 *
 * ⚠️ API shape inconsistency:
 * - createYardGoal/updateYardGoal normally return
 *   `{ goal: { data: Goal, goalId: string, yardId: string } }`.
 * - legacy write responses may return `goal.data.goal`.
 * - getYardGoals returns `{ goals: [{ data: { goal: Goal }, ... }] }`.
 *
 * `GoalEnvelope` is the double-nested getYardGoals shape. Goal write endpoints
 * accept either the direct or nested data variant modeled below.
 */
export interface CreateGoalResponse {
  goal: {
    data: Goal | { goal: Goal };
    goalId: string;
    yardId: string;
  };
}

export interface UpdateGoalParameters extends GoalParameters {
  /**
   * Existing failure policy from the goal being updated. This property is
   * required so every full-parameter replacement consciously preserves it.
   * `undefined` keeps legacy goals unchanged; the UI does not restore policy.
   */
  failureMode: FailureMode | undefined;
}

export interface UpdateGoalRequest {
  goalId: GoalId;
  parameters?: UpdateGoalParameters;
  requester: string;
  /**
   * Optimistic-locking guard. When present, the engine rejects the
   * mutation with `ConditionalCheckFailedException` if the goal's current
   * version differs — closing the read-modify-write race where a WS update
   * (or another operator) moved the goal between our read and our write.
   * Threaded from the goal's `version` at the call site.
   */
  expectedVersionId?: number;
  /**
   * Operator-supplied free-text justification for the override (smithy
   * `overrideReason: String`, response-mirrored on `Goal.overrideReason`).
   * NOT the `GoalStatusReason` enum — that is engine-set and output-only.
   * Release sends the resolved reason label here on its update branches.
   */
  overrideReason?: string;
}

/**
 * updateYardGoal uses the same write envelope variants as createYardGoal:
 * `goal.data` is normally the Goal directly, while legacy responses may wrap
 * it once more under `goal.data.goal`.
 */
export interface UpdateGoalResponse {
  goal: CreateGoalResponse["goal"];
}

export interface RequestCancelGoalRequest {
  goalId: GoalId;
  requester: string;
  cancellationReason?: string;
  /** Optimistic-locking guard — see `UpdateGoalRequest.expectedVersionId`. */
  expectedVersionId?: number;
}

/**
 * Wire shape for cancel/hold/release goal mutation responses.
 *
 * Distinct from the double-nested `GoalEnvelope` used by getYardGoals.
 * The cancel/hold/release endpoints emit a SINGLE-nested envelope —
 * `{ data: Goal, goalId, yardId }`.
 *
 * `transformResponse` in `ryteApis.ts` unwraps via `raw?.goal?.data`
 * to produce a real Goal for downstream consumers (`applyGoalFromMutation`).
 * Without the unwrap, dispatching `res.goal` straight into `upsertGoal`
 * pollutes the slice with envelope-shaped entries whose `demandId` lives
 * at `wrapper.data.demandId` — the projector then crashes on
 * `resolveAllDemandIds(undefined)`.
 */
interface GoalMutationResultEnvelope {
  data: Goal;
  goalId: string;
  yardId: string;
}

export interface RequestCancelGoalResponse {
  goal: GoalMutationResultEnvelope;
  cancelRequestAccepted: boolean;
}

/** Post-`transformResponse` shape — `goal` is unwrapped to a real Goal record. */
export interface NormalizedRequestCancelGoalResponse {
  goal: Goal | null;
  cancelRequestAccepted: boolean;
}

export interface RequestHoldGoalRequest {
  goalId: GoalId;
  requester: string;
  holdReason?: string;
  /** Optimistic-locking guard — see `UpdateGoalRequest.expectedVersionId`. */
  expectedVersionId?: number;
}

export interface RequestHoldGoalResponse {
  goal: GoalMutationResultEnvelope;
  holdRequestAccepted: boolean;
}

/** Post-`transformResponse` shape — `goal` is unwrapped to a real Goal record. */
export interface NormalizedRequestHoldGoalResponse {
  goal: Goal | null;
  holdRequestAccepted: boolean;
}

export interface RequestReleaseGoalRequest {
  goalId: GoalId;
  requester: string;
  /** Optimistic-locking guard — see `UpdateGoalRequest.expectedVersionId`. */
  expectedVersionId?: number;
}

export interface RequestReleaseGoalResponse {
  goal: GoalMutationResultEnvelope;
  releaseRequestAccepted: boolean;
}

/** Post-`transformResponse` shape — `goal` is unwrapped to a real Goal record. */
export interface NormalizedRequestReleaseGoalResponse {
  goal: Goal | null;
  releaseRequestAccepted: boolean;
}

/**
 * Smithy: POST /getYardGoals — returns goals in Lambda envelope format.
 * Each goal is wrapped: { data: Goal, goalId: string, yardId: string }.
 */
export interface GetYardGoalsRequest {
  yardId: string;
  startEpochMs: number;
  endEpochMs: number;
}

export interface GetYardGoalsResponse {
  goals: GoalEnvelope[];
}

// ─── Error Types ─────────────────────────────────────────────────────────────

export interface InvalidArgumentException {
  __type: "InvalidArgumentException";
  message: string;
}

export interface EntityNotFoundException {
  __type: "EntityNotFoundException";
  message: string;
}

export interface ConditionalCheckFailedException {
  __type: "ConditionalCheckFailedException";
  message: string;
}

export interface TransitionNotAllowedException {
  __type: "TransitionNotAllowedException";
  message: string;
}

export interface InternalServiceException {
  __type: "InternalServiceException";
  message: string;
}

export type RyteApiError =
  | InvalidArgumentException
  | EntityNotFoundException
  | ConditionalCheckFailedException
  | TransitionNotAllowedException
  | InternalServiceException;
